import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ENQUIRY_TYPES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#12281a] border border-[#b38b47]/40 px-4 py-3 text-sm text-[#f7f5f0] placeholder:text-[#f7f5f0]/35 focus:outline-none focus:border-[#b38b47] transition-colors";
const labelCls = "block text-xs uppercase tracking-wider text-[#f7f5f0]/80 mb-2";

export function EnquiryForm({ defaultType = "project-enquiry", compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    enquiry_type: defaultType,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        enquiry_type: form.enquiry_type,
        message: form.message.trim() || null,
      });
      setDone(true);
      toast.success("Thank you. Our leadership desk will reach out to you shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly at 8669361526.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#183523] p-10 border border-[#b38b47]/30 text-center" data-testid="enquiry-success">
        <div className="chapter-num mb-4">✦</div>
        <h3 className="font-monument text-2xl text-[#f7f5f0] mb-3">Enquiry Received</h3>
        <p className="text-sm text-[#f7f5f0]/70 max-w-md mx-auto">
          Thank you, {form.name.split(" ")[0]}. Your enquiry has been recorded with our executive desk.
          We will connect with you on {form.phone} shortly.
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({ name: "", phone: "", email: "", enquiry_type: defaultType, message: "" });
          }}
          className="mt-6 text-xs uppercase tracking-widest text-[#b38b47] hover:text-[#f7f5f0] transition-colors"
          data-testid="enquiry-another-button"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6 bg-[#183523] p-8 border border-[#b38b47]/30" data-testid="enquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={labelCls} htmlFor="enquiry-name">Full Name</label>
          <input
            id="enquiry-name"
            type="text"
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className={inputCls}
            data-testid="enquiry-name-input"
            required
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="enquiry-phone">Contact Phone</label>
          <input
            id="enquiry-phone"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+91"
            className={inputCls}
            data-testid="enquiry-phone-input"
            required
          />
        </div>
      </div>

      {!compact && (
        <div>
          <label className={labelCls} htmlFor="enquiry-email">Email (optional)</label>
          <input
            id="enquiry-email"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className={inputCls}
            data-testid="enquiry-email-input"
          />
        </div>
      )}

      <div>
        <label className={labelCls} htmlFor="enquiry-type">Nature of Enquiry</label>
        <select
          id="enquiry-type"
          value={form.enquiry_type}
          onChange={set("enquiry_type")}
          className={inputCls}
          data-testid="enquiry-type-select"
        >
          {ENQUIRY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelCls} htmlFor="enquiry-message">Requirement Note</label>
        <textarea
          id="enquiry-message"
          rows={compact ? 2 : 3}
          value={form.message}
          onChange={set("message")}
          placeholder="Brief details about your requirement, land parcel, or timeline..."
          className={inputCls}
          data-testid="enquiry-message-input"
        />
      </div>

      <div className="text-right">
        <button
          type="submit"
          disabled={submitting}
          className="px-8 py-3.5 bg-[#b38b47] hover:bg-[#9a7638] disabled:opacity-60 text-[#12281a] text-xs uppercase tracking-widest font-bold transition-colors"
          data-testid="enquiry-submit-button"
        >
          {submitting ? "Submitting..." : "Submit Enquiry"}
        </button>
      </div>
    </form>
  );
}
